import {deleteJSON, getJSON, postJSON, postFormData, putJSON} from './http';

const parseResponse = async (response, fallback) => {
  if (response.status >= 200 && response.status < 300) {
    if (response.status === 204) {
      return null;
    }
    return response.json();
  }

  let message = fallback;
  try {
    const body = await response.json();
    message = (body.error && body.error.message) || body.error || body.message || fallback;
  } catch (error) {
    // Ответы 404/500 могут не содержать JSON.
  }
  const requestError = new Error(message);
  requestError.status = response.status;
  throw requestError;
};

export const createIdempotencyKey = scope => (
  `${scope}-${Date.now()}-${Math.random().toString(16).slice(2)}`
);

const authHeaders = (authorization, idempotencyKey) => ({
  authorization,
  ...(idempotencyKey ? {'Idempotency-Key': idempotencyKey} : {}),
});

export const getKycQueue = (authorization, status = 'pending') => (
  getJSON('rental-management/kyc', authHeaders(authorization), {status})
    .then(response => parseResponse(response, 'Не удалось загрузить очередь документов'))
);

export const getKycSubmission = (authorization, submissionId) => (
  getJSON(`rental-management/kyc/${submissionId}`, authHeaders(authorization), {})
    .then(response => parseResponse(response, 'Не удалось загрузить комплект документов'))
);

export const reviewKyc = (authorization, submissionId, decision, reason, key) => (
  postJSON(
    `rental-management/kyc/${submissionId}/${decision}`,
    authHeaders(authorization, key),
    {reason: reason || null},
  ).then(response => parseResponse(response, 'Не удалось сохранить решение'))
);

export const getProtectedFile = (authorization, fileId, download = false) => (
  getJSON(
    `rental-management/files/${fileId}`,
    authHeaders(authorization),
    download ? {action: 'download'} : {},
  ).then(async response => {
    if (response.status >= 200 && response.status < 300) {
      return response.blob();
    }
    return parseResponse(response, 'Не удалось загрузить защищённый файл');
  })
);

export const getRentalVehicles = (authorization, status = '') => (
  getJSON('rental-management/vehicles', authHeaders(authorization), status ? {status} : {})
    .then(response => parseResponse(response, 'Не удалось загрузить автопарк'))
);

export const createRentalVehicle = (authorization, payload) => (
  postJSON('rental-management/vehicles', authHeaders(authorization), payload)
    .then(response => parseResponse(response, 'Не удалось создать автомобиль'))
);

export const updateRentalVehicle = (authorization, vehicleId, payload) => (
  putJSON(`rental-management/vehicles/${vehicleId}`, authHeaders(authorization), payload)
    .then(response => parseResponse(response, 'Не удалось сохранить автомобиль'))
);

export const getVehicleTelematics = (authorization, vehicleId, period = {}) => (
  getJSON(`rental-management/vehicles/${vehicleId}/telematics`, authHeaders(authorization), period)
    .then(response => parseResponse(response, 'Не удалось загрузить телеметрию'))
);

export const getVehicleDevice = (authorization, vehicleId) => (
  getJSON(`rental-management/vehicles/${vehicleId}/device`, authHeaders(authorization), {})
    .then(response => parseResponse(response, 'Не удалось обновить связь с устройством'))
);

const vegaResponse = async (request, fallback) => {
  let timer;
  try {
    return await Promise.race([
      request.then(response => parseResponse(response, fallback)),
      new Promise((resolve, reject) => { timer = setTimeout(() => reject(new Error('Ответ VEGA API не получен')), 10000); }),
    ]);
  } finally { clearTimeout(timer); }
};
export const getVegaDevice = (authorization, imei) => vegaResponse(
  getJSON(`rental-management/devices/${imei}`, authHeaders(authorization), {}), 'Не удалось загрузить устройство VEGA'
);
export const getVegaMessages = (authorization, imei, before) => vegaResponse(
  getJSON(`rental-management/devices/${imei}/messages`, authHeaders(authorization), before ? {before} : {}), 'Не удалось загрузить сообщения VEGA'
);
export const previewVegaCommand = (authorization, imei, command) => vegaResponse(
  postJSON(`rental-management/devices/${imei}/commands/preview`, authHeaders(authorization), {command}), 'Не удалось проверить команду'
);
export const sendVegaCommand = (authorization, imei, command, key) => vegaResponse(
  postJSON(`rental-management/devices/${imei}/commands`, authHeaders(authorization, key), {command}), 'Не удалось отправить команду'
);
export const getVegaCommand = (authorization, imei, id) => vegaResponse(
  getJSON(`rental-management/devices/${imei}/commands/${id}`, authHeaders(authorization), {}), 'Не удалось получить результат команды'
);

export const configureVehicleDevice = (authorization, vehicleId, payload) => (
  putJSON(`rental-management/vehicles/${vehicleId}/device`, authHeaders(authorization), payload)
    .then(response => parseResponse(response, 'Не удалось сохранить устройство'))
);

// Ошибка команды — тоже известный результат; сетевые ошибки остаются исключениями.
export const controlVehicleDoor = async (authorization, vehicleId, action, key) => {
  if (!['lock', 'unlock'].includes(action)) throw new Error('Неизвестная команда дверей');
  let timer;
  try {
    return await Promise.race([
      (async () => {
        const response = await postJSON(`rental-management/vehicles/${vehicleId}/${action}`, authHeaders(authorization, key), {});
        const result = await response.json();
        if (!result || !['success', 'error'].includes(result.status)) throw new Error('Результат команды неизвестен');
        return result;
      })(),
      new Promise((resolve, reject) => { timer = setTimeout(() => reject(new Error('Результат команды неизвестен')), 10000); }),
    ]);
  } finally { clearTimeout(timer); }
};

export const getRentals = (authorization, filters = {}) => (
  getJSON('rental-management/rentals', authHeaders(authorization), filters)
    .then(response => parseResponse(response, 'Не удалось загрузить аренды'))
);

export const getRental = (authorization, rentalId) => (
  getJSON(`rental-management/rentals/${rentalId}`, authHeaders(authorization), {})
    .then(response => parseResponse(response, 'Не удалось загрузить аренду'))
);

export const postRentalAction = (authorization, rentalId, action, payload, key) => (
  postJSON(
    `rental-management/rentals/${rentalId}/${action}`,
    authHeaders(authorization, key),
    payload,
  ).then(response => parseResponse(response, 'Не удалось выполнить действие с арендой'))
);

export const createRentalCharge = (authorization, rentalId, fields, file, key) => {
  const body = new FormData();
  Object.keys(fields).forEach(name => body.append(name, fields[name]));
  body.append('file', file);
  return postFormData(
    `rental-management/rentals/${rentalId}/charges`,
    authHeaders(authorization, key),
    body,
  ).then(response => parseResponse(response, 'Не удалось создать начисление'));
};

/* --- Документы аренды и карточка клиента --- */

export const getRentalDocuments = (authorization, rentalId) => (
  getJSON(`rental-management/rentals/${rentalId}/documents`, authHeaders(authorization), {})
    .then(response => parseResponse(response, 'Не удалось загрузить документы аренды'))
);

export const uploadRentalDocument = (authorization, rentalId, fields, file) => {
  const body = new FormData();
  Object.keys(fields).forEach(name => body.append(name, fields[name]));
  body.append('file', file);
  return postFormData(
    `rental-management/rentals/${rentalId}/documents`,
    authHeaders(authorization),
    body,
  ).then(response => parseResponse(response, 'Не удалось загрузить документ'));
};

export const deleteRentalDocument = (authorization, rentalId, documentId, reason) => (
  deleteJSON(
    `rental-management/rentals/${rentalId}/documents/${documentId}`,
    authHeaders(authorization),
    {reason},
  ).then(response => parseResponse(response, 'Не удалось удалить документ'))
);

export const getRentalClient = (authorization, userId) => (
  getJSON(`rental-management/clients/${userId}`, authHeaders(authorization), {})
    .then(response => parseResponse(response, 'Не удалось загрузить карточку клиента'))
);

/* --- Документы автомобиля: СТС, ОСАГО, КАСКО --- */

export const getVehicleDocuments = (authorization, vehicleId) => (
  getJSON(`rental-management/vehicles/${vehicleId}/documents`, authHeaders(authorization), {})
    .then(response => parseResponse(response, 'Не удалось загрузить документы автомобиля'))
);

export const uploadVehicleDocument = (authorization, vehicleId, fields, file) => {
  const body = new FormData();
  Object.keys(fields).forEach(name => {
    if (fields[name] !== null && fields[name] !== undefined && fields[name] !== '') {
      body.append(name, fields[name]);
    }
  });
  body.append('file', file);
  return postFormData(
    `rental-management/vehicles/${vehicleId}/documents`,
    authHeaders(authorization),
    body,
  ).then(response => parseResponse(response, 'Не удалось загрузить документ автомобиля'));
};

export const deleteVehicleDocument = (authorization, vehicleId, documentId, reason) => (
  deleteJSON(
    `rental-management/vehicles/${vehicleId}/documents/${documentId}`,
    authHeaders(authorization),
    {reason},
  ).then(response => parseResponse(response, 'Не удалось удалить документ автомобиля'))
);

/**
 * Пересъёмка отдельных документов KYC. Отличается от отклонения комплекта:
 * плохой снимок исправим, и переснимать всю пачку клиент не должен.
 */
export const requestKycRevision = (authorization, submissionId, documents) => (
  postJSON(
    `rental-management/kyc/${submissionId}/revision`,
    authHeaders(authorization),
    {documents},
  ).then(response => parseResponse(response, 'Не удалось отправить документы на пересъёмку'))
);

export const retryRentalCharge = (authorization, chargeId, key) => (
  postJSON(
    `rental-management/charges/${chargeId}/retry`,
    authHeaders(authorization, key),
    {},
  ).then(response => parseResponse(response, 'Не удалось повторить списание'))
);

export const setRentalManagerRole = (authorization, managerId, key) => (
  postJSON(
    `rental-management/managers/${managerId}/role`,
    authHeaders(authorization, key),
    {role: 'rental-manager'},
  ).then(response => parseResponse(response, 'Не удалось назначить роль'))
);

/* --- Фаза 1: настройки, пункты выдачи и зоны возврата --- */

export const getRentalSettings = authorization => (
  getJSON('rental-management/settings', authHeaders(authorization), {})
    .then(response => parseResponse(response, 'Не удалось загрузить настройки проката'))
);

export const updateRentalSettings = (authorization, payload) => (
  putJSON('rental-management/settings', authHeaders(authorization), payload)
    .then(response => parseResponse(response, 'Не удалось сохранить настройки проката'))
);

export const getRentalLocations = authorization => (
  getJSON('rental-management/locations', authHeaders(authorization), {})
    .then(response => parseResponse(response, 'Не удалось загрузить пункты выдачи'))
);

export const createRentalLocation = (authorization, payload) => (
  postJSON('rental-management/locations', authHeaders(authorization), payload)
    .then(response => parseResponse(response, 'Не удалось создать пункт выдачи'))
);

export const updateRentalLocation = (authorization, locationId, payload) => (
  putJSON(`rental-management/locations/${locationId}`, authHeaders(authorization), payload)
    .then(response => parseResponse(response, 'Не удалось сохранить пункт выдачи'))
);

export const getRentalReturnZones = (authorization, locationId) => (
  getJSON('rental-management/return-zones', authHeaders(authorization), locationId ? {location_id: locationId} : {})
    .then(response => parseResponse(response, 'Не удалось загрузить зоны возврата'))
);

export const createRentalReturnZone = (authorization, payload) => (
  postJSON('rental-management/return-zones', authHeaders(authorization), payload)
    .then(response => parseResponse(response, 'Не удалось создать зону возврата'))
);

export const updateRentalReturnZone = (authorization, zoneId, payload) => (
  putJSON(`rental-management/return-zones/${zoneId}`, authHeaders(authorization), payload)
    .then(response => parseResponse(response, 'Не удалось сохранить зону возврата'))
);

export const getRentalDashboard = (authorization, locationId) => (
  getJSON('rental-management/dashboard', authHeaders(authorization), locationId ? {location_id: locationId} : {})
    .then(response => parseResponse(response, 'Не удалось загрузить дашборд проката'))
);

export const deleteRentalReturnZone = (authorization, zoneId) => (
  deleteJSON(`rental-management/return-zones/${zoneId}`, authHeaders(authorization))
    .then(response => parseResponse(response, 'Не удалось удалить зону возврата'))
);

export const getRentalVehicleBlocks = (authorization, filters = {}) => (
  getJSON('rental-management/vehicle-blocks', authHeaders(authorization), filters)
    .then(response => parseResponse(response, 'Не удалось загрузить блокировки'))
);

export const createRentalVehicleBlock = (authorization, payload) => (
  postJSON('rental-management/vehicle-blocks', authHeaders(authorization), payload)
    .then(response => parseResponse(response, 'Не удалось создать блокировку'))
);

export const deleteRentalVehicleBlock = (authorization, blockId) => (
  deleteJSON(`rental-management/vehicle-blocks/${blockId}`, authHeaders(authorization))
    .then(response => parseResponse(response, 'Не удалось снять блокировку'))
);

/* --- Календарь занятости и платёжные операции --- */

export const getRentalCalendar = (authorization, params = {}) => (
  getJSON('rental-management/calendar', authHeaders(authorization), params)
    .then(response => parseResponse(response, 'Не удалось загрузить календарь'))
);

export const getPaymentOperations = (authorization, filters = {}) => (
  getJSON('rental-management/payment-operations', authHeaders(authorization), filters)
    .then(response => parseResponse(response, 'Не удалось загрузить платёжные операции'))
);

export const settlePaymentOperation = (authorization, operationId, payload, key) => (
  postJSON(
    `rental-management/payment-operations/${operationId}/settle`,
    authHeaders(authorization, key),
    payload,
  ).then(response => parseResponse(response, 'Не удалось закрыть операцию'))
);

/* --- Внешние проверки клиента --- */

export const getVerificationCatalog = authorization => (
  getJSON('rental-management/verification/catalog', authHeaders(authorization), {})
    .then(response => parseResponse(response, 'Не удалось загрузить каталог проверок'))
);

export const getVerificationChecks = (authorization, userId, submissionId) => (
  getJSON(
    `rental-management/verification/${userId}`,
    authHeaders(authorization),
    submissionId ? {submission_id: submissionId} : {},
  ).then(response => parseResponse(response, 'Не удалось загрузить результаты проверок'))
);

export const requestVerificationChecks = (authorization, userId, payload, key) => (
  postJSON(`rental-management/verification/${userId}`, authHeaders(authorization, key), payload)
    .then(response => parseResponse(response, 'Не удалось запросить проверку'))
);

export const getVerificationRisk = (authorization, userId) => (
  getJSON(`rental-management/verification/${userId}/risk`, authHeaders(authorization), {})
    .then(response => parseResponse(response, 'Не удалось получить оценку риска'))
);

export const recomputeVerificationRisk = (authorization, userId) => (
  postJSON(`rental-management/verification/${userId}/risk`, authHeaders(authorization), {})
    .then(response => parseResponse(response, 'Не удалось пересчитать оценку риска'))
);

export const refreshVerificationCheck = (authorization, checkId, key) => (
  postJSON(
    `rental-management/verification/checks/${checkId}/refresh`,
    authHeaders(authorization, key),
    {},
  ).then(response => parseResponse(response, 'Не удалось обновить результат проверки'))
);

/* --- Тестовые инструменты dev-стенда (сервер отвечает 404, если выключены) --- */

export const getRentalTestTools = authorization => (
  getJSON('rental-management/test-tools', authHeaders(authorization), {})
    .then(response => parseResponse(response, 'Тестовые инструменты недоступны'))
);

export const overrideRentalForTesting = (authorization, rentalId, payload) => (
  putJSON(`rental-management/test-tools/rentals/${rentalId}`, authHeaders(authorization), payload)
    .then(response => parseResponse(response, 'Не удалось применить тестовые изменения'))
);

export const cancelRentalPaymentForTesting = (authorization, rentalId, paymentId) => (
  postJSON(
    `rental-management/test-tools/rentals/${rentalId}/payments/${paymentId}/cancel`,
    authHeaders(authorization),
    {},
  ).then(response => parseResponse(response, 'Не удалось отменить платёж'))
);
